import { PAYEX_URL } from 'constants/config';
import { decamelizeKeys } from 'humps';
import type { Payment } from 'models/payment';
import { fetchWithAuthentication } from 'utils/fetchJson/fetchJson';

import type {
  ChangePaymentMethodForCardsPayload,
  ChangePaymentMethodPayload,
  DeleteFileRequestParams,
  DeleteFileResponse,
  DocVerifyResult,
  FetchByIdRequestParams,
  FetchDocumentsCollections,
  FetchDocVerifyRequestParams,
  FetchFileUploadURLRequestParams,
  FetchHoldReasonsRequestParams,
  FetchPaymentRefundsRequestParams,
  FetchProofsRequestParams,
  FetchSmsNotificationRequestParams,
  FetchUserPaymentsRequestParams,
  FetchUserPaymentsResponse,
  GenerateFilenameResponse,
  GenerateFilenameUrlRequestParams,
  HoldReasonsResponse,
  PaymentNotificationRequestParams,
  PaymentNotificationResponse,
  PaymentRefundsResponse,
  ProofResponseBody,
  RepeatPaymentPayload,
  SMSNotificationResponse,
  UpdatePaymentRequestBody,
  UpdateSMSNotificationRequestParams,
  UploadFileToUrlRequestParams,
} from './types';

export type PaymentsService = {
  update: (payload: UpdatePaymentRequestBody) => Promise<Payment>;
  repeatPaymentOrder: (payload: RepeatPaymentPayload) => Promise<Payment>;
  changePaymentMethod: (
    payload: ChangePaymentMethodPayload,
  ) => Promise<Payment>;
  changePaymentMethodForCards: (
    payload: ChangePaymentMethodForCardsPayload,
  ) => Promise<void>;
  fetchById: (params: FetchByIdRequestParams) => Promise<Payment>;
  fetchLastRepeatable: () => Promise<Payment>;
  fetchUserPayments: (
    payload: FetchUserPaymentsRequestParams,
  ) => Promise<FetchUserPaymentsResponse>;
  fetchNotification: (
    params: FetchByIdRequestParams,
  ) => Promise<PaymentNotificationResponse>;
  updateNotification: (
    payload: PaymentNotificationRequestParams,
  ) => Promise<PaymentNotificationResponse>;
  fetchDocumentsCollections: (
    payload: FetchDocumentsCollections,
  ) => Promise<Record<string, unknown>>;
  fetchDocVerify: (
    payload: FetchDocVerifyRequestParams,
  ) => Promise<DocVerifyResult>;
  fetchSMSNotification: (
    payload: FetchSmsNotificationRequestParams,
  ) => Promise<SMSNotificationResponse>;
  updateSMSNotification: (
    payload: UpdateSMSNotificationRequestParams,
  ) => Promise<SMSNotificationResponse>;
  fetchProofs: (
    payload: FetchProofsRequestParams,
  ) => Promise<ProofResponseBody>;
  fetchHoldReasons: (
    payload: FetchHoldReasonsRequestParams,
  ) => Promise<HoldReasonsResponse>;
  generateProofUrl: (
    payload: GenerateFilenameUrlRequestParams,
  ) => Promise<GenerateFilenameResponse>;
  generateDocumentUrl: (
    payload: GenerateFilenameUrlRequestParams,
  ) => Promise<GenerateFilenameResponse>;
  deleteFile: (payload: DeleteFileRequestParams) => Promise<DeleteFileResponse>;
  fetchFileUploadURL: (
    payload: FetchFileUploadURLRequestParams,
  ) => Promise<{ url?: string }>;
  fetchPaymentRefunds: (
    payload: FetchPaymentRefundsRequestParams,
  ) => Promise<PaymentRefundsResponse>;
  uploadFileToUrl: (payload: UploadFileToUrlRequestParams) => Promise<void>;
};

export const payments: PaymentsService = {
  update: ({ id, token, ...payload }) => {
    const path = `${PAYEX_URL}/payments/${id}?token=${token}`;
    const body = JSON.stringify(payload);
    const options = {
      method: 'PATCH',
      body,
    };

    return fetchWithAuthentication(path, options);
  },
  repeatPaymentOrder: ({ id, ...payload }) => {
    const path = `${PAYEX_URL}/${id}/repeat_payment_order`;
    const body = JSON.stringify(decamelizeKeys(payload));
    const options = { method: 'POST', body };
    return fetchWithAuthentication(path, options);
  },
  changePaymentMethod: ({
    paymentId,
    paymentToken,
    orderId,
    orderToken,
    fingerprint,
    locale,
    captchaResponse,
  }) => {
    const path = `${PAYEX_URL}/${paymentId}/change_payment_method`;
    const payload = {
      orderId,
      orderToken,
      paymentToken,
      fingerprint,
      locale,
      ...(captchaResponse && { captchaResponse }),
    };
    const body = JSON.stringify(decamelizeKeys(payload));
    const options = { method: 'POST', body };

    return fetchWithAuthentication(path, options);
  },
  changePaymentMethodForCards: ({
    paymentId,
    paymentToken,
    orderId,
    orderToken,
    fingerprint,
  }) => {
    const path = `${PAYEX_URL}/${paymentId}/change_payment_method_for_cards`;
    const payload = {
      orderId,
      orderToken,
      paymentToken,
      fingerprint,
    };
    const body = JSON.stringify(decamelizeKeys(payload));
    const options = { method: 'POST', body };

    return fetchWithAuthentication(path, options);
  },
  fetchById: ({ id, locale }) => {
    const path = `${PAYEX_URL}/payments/${id}?locale=${locale}`;
    const options = {
      method: 'GET',
    };

    return fetchWithAuthentication(path, options);
  },
  fetchLastRepeatable: () => {
    const path = `${PAYEX_URL}/payments/last-repeatable`;
    const options = {
      method: 'GET',
    };

    return fetchWithAuthentication(path, options);
  },
  fetchUserPayments: ({ perPage = 5, page = 1 } = {}) => {
    const path = `${PAYEX_URL}/payments?per_page=${perPage}&page=${
      page < 1 ? 1 : page
    }`;
    const options = {
      method: 'GET',
    };

    return fetchWithAuthentication(path, options);
  },
  fetchNotification: ({ id, token }) => {
    const path = `${PAYEX_URL}/payments/${id}/notifications?token=${token}`;
    const options = {
      method: 'GET',
    };

    return fetchWithAuthentication(path, options);
  },
  updateNotification: ({ id, active, number, token }) => {
    const path = `${PAYEX_URL}/payments/${id}/notifications?token=${token}`;
    const payload = { active, number };
    const body = JSON.stringify(payload);
    const options = { method: 'POST', body };

    return fetchWithAuthentication(path, options);
  },
  fetchDocumentsCollections: ({ id, token, locale }) => {
    const url = `${PAYEX_URL}/payments/${id}/documents_collections?token=${encodeURIComponent(token)}&locale=${encodeURIComponent(locale)}`;
    const options = { method: 'GET' };

    return fetchWithAuthentication(url, options);
  },
  fetchDocVerify: ({
    collectionId,
    collectionToken,
    documentId,
    fileId,
    paymentId,
    paymentToken,
  }) => {
    const params = new URLSearchParams({ token: collectionToken });
    if (paymentId) {
      params.set('payment_id', paymentId);
    }
    if (paymentToken) {
      params.set('payment_token', paymentToken);
    }
    const url = `${PAYEX_URL}/document_collections/${collectionId}/documents/${documentId}/files/${fileId}/doc_verify?${params.toString()}`;
    const options = { method: 'GET' };
    return fetchWithAuthentication(url, options);
  },
  fetchSMSNotification: ({ url }) => {
    const options = { method: 'GET' };

    return fetchWithAuthentication(url, options);
  },
  updateSMSNotification: ({ number, active, url }) => {
    let options;

    switch (active) {
      case true:
        const payload = { number };
        const body = JSON.stringify(payload);
        options = { method: 'POST', body };
        break;
      case false:
        options = { method: 'DELETE' };
        break;
    }

    return fetchWithAuthentication(url, options);
  },
  fetchProofs: ({ id, token }) => {
    const path = `${PAYEX_URL}/payments/${id}/proofs?token=${token}`;
    const options = { method: 'GET' };

    return fetchWithAuthentication(path, options);
  },
  fetchHoldReasons: ({ id, token }) => {
    const path = `${PAYEX_URL}/payments/${id}/hold-reasons?token=${token}`;
    const options = { method: 'GET' };

    return fetchWithAuthentication(path, options);
  },
  generateProofUrl: ({ id, token, filename }) => {
    const path = `${PAYEX_URL}/payments/${id}/proofs/upload_url?token=${token}`;
    const payload = { filename };
    const body = JSON.stringify(payload);
    const options = { method: 'POST', body };

    return fetchWithAuthentication(path, options);
  },
  generateDocumentUrl: ({ id, token, filename }) => {
    const path = `${PAYEX_URL}/payments/${id}/documents/upload_url?token=${token}`;
    const payload = { filename };
    const body = JSON.stringify(payload);
    const options = { method: 'POST', body };

    return fetchWithAuthentication(path, options);
  },
  fetchFileUploadURL: ({ documentUploadURL }) => {
    const options = { method: 'POST' };

    return fetchWithAuthentication(documentUploadURL, options);
  },
  deleteFile: ({ url }) => {
    const path = url;
    const options = { method: 'DELETE' };

    return fetchWithAuthentication(path, options);
  },
  uploadFileToUrl: ({ file, url, info }) => {
    const body = new FormData();
    body.append('file', file);
    body.append('info', JSON.stringify(info));

    const path = url;
    const options = { method: 'POST', body };

    return fetchWithAuthentication(path, options);
  },
  fetchPaymentRefunds: ({ paymentId }) => {
    const path = `${PAYEX_URL}/payments/${paymentId}/refunds`;
    const options = { method: 'GET' };

    return fetchWithAuthentication(path, options);
  },
};
